import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
} from "@mui/material";
import { CustomInput } from "../../../components/Common/CustomInputs";
import CustomTagInput from "../../../components/Common/CustomTagInput";
import { colors, fonts } from "../../../theme/theme";

const EMPTY = { firstName: "", lastName: "", email: "", city: "", country: "", hourlyRate: "", specialities: [] };
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddPractitionerModal = ({ open, onClose, onCreate }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError("First name, last name and email are required.");
      return;
    }
    if (!emailRe.test(form.email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setSaving(true);
    const ok = await onCreate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      country: form.country.trim(),
      hourlyRate: form.hourlyRate,
      specialities: form.specialities,
    });
    setSaving(false);
    if (ok) {
      setForm(EMPTY);
      onClose();
    }
  };

  const close = () => {
    if (saving) return;
    setForm(EMPTY);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: fonts.gotham, color: colors.primary }}>
        Add Practitioner
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            pt: 1,
            "& .MuiOutlinedInput-root": { width: "100% !important" },
          }}
        >
          <CustomInput label="First Name" required value={form.firstName} setValue={(v) => set("firstName", v)} noIcon />
          <CustomInput label="Last Name" required value={form.lastName} setValue={(v) => set("lastName", v)} noIcon />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomInput label="Email" required value={form.email} setValue={(v) => set("email", v)} noIcon placeholder="practitioner@example.com" />
          </Box>
          <CustomInput label="City" value={form.city} setValue={(v) => set("city", v)} noIcon />
          <CustomInput label="Country" value={form.country} setValue={(v) => set("country", v)} noIcon />
          <CustomInput label="Hourly Rate (USD)" type="number" value={form.hourlyRate} setValue={(v) => set("hourlyRate", v)} noIcon />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomTagInput
              label="Specialities"
              hashTags={form.specialities}
              setHashTags={(v) => set("specialities", v)}
              placeholder="Add a speciality and press Enter"
            />
          </Box>
          {error && (
            <Box sx={{ gridColumn: "1 / -1", color: "#C0392B", fontFamily: fonts.quicksand, fontSize: 14 }}>
              {error}
            </Box>
          )}
          <Box sx={{ gridColumn: "1 / -1", color: colors.textLight, fontFamily: fonts.quicksand, fontSize: 13 }}>
            The practitioner will receive an email to set their password. Account starts as Pending.
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={close}
          disabled={saving}
          sx={{ textTransform: "none", fontFamily: fonts.quicksand, borderColor: colors.lightBorder, color: colors.text }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving}
          sx={{ textTransform: "none", fontFamily: fonts.quicksand, backgroundColor: colors.secondary, "&:hover": { backgroundColor: "#1f2e3e" } }}
        >
          {saving ? "Creating..." : "Create & Send Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPractitionerModal;
